import {
  extractQuickNoteEntities,
  normalizeQuickNoteReviewRows,
} from '../../../utils/quickNoteClassification';
import { groupRowsForReview } from '../../../utils/reviewRowGrouping';
import {
  hasStrongSchoolStructureForCategory,
  normalizeImportCategory,
} from './importLogsParsingClassification';
import {
  detectNaturalCaptureInputShape,
} from './naturalCaptureChunkingCore';
import { buildNaturalCaptureCategoryDecision } from './naturalCaptureCategoryCore';
import { runNaturalCaptureQuickLogAdapter } from './naturalCaptureQuickLogAdapter';
import {
  formatLocalTimeForStorage,
  parseDateForStorage,
} from './importLogsParsingTime';
import {
  applyNaturalCaptureCategoryDecisions,
  emitNaturalCaptureDecisionAudit,
} from './importLogsAdapterAudit';
import {
  extractImportDateContexts,
} from './importLogsDateContext';
import {
  runImportedFileTextAdapter,
} from './importLogsFileImportAdapter';
import {
  normalizeImportedFileReviewRow,
  normalizeImportedLongNoteReviewRow,
  normalizeImportedSchoolReviewRow,
} from './importLogsReviewNormalization';
import {
  isLongNoteEmailText,
  stripLongNoteEmailBoilerplate,
} from './importLogsLongNoteParsing';
import { runLongNoteEmailAdapter } from './importLogsLongNoteAdapter';
import { filterQuickNoteCaptureableEntities } from './importLogsQuickStructuredAdapter';
import {
  buildNaturalCaptureSchoolRows,
  runSchoolPastedTextAdapter,
} from './importLogsSchoolAdapter';
import { normalizeTextForImport } from './importLogsParsingText';

export {
  formatLocalTimeForStorage,
  parseDateForStorage,
  resolveImportPreviewTiming,
} from './importLogsParsingTime';
export {
  applyImportDateContextToImportedRows,
  extractImportDateContexts,
} from './importLogsDateContext';
export { buildImportedFileTextReviewRows } from './importLogsFileImportAdapter';
export {
  normalizeImportedFileReviewRow,
  normalizeImportedLongNoteReviewRow,
  normalizeImportedSchoolReviewRow,
} from './importLogsReviewNormalization';
export {
  buildNaturalCaptureLongNoteRows,
  isLongNoteEmailText,
  stripLongNoteEmailBoilerplate,
} from './importLogsLongNoteParsing';
export {
  buildQuickNoteStructuredReviewRows,
  filterQuickNoteCaptureableEntities,
  runQuickLogStructuredAdapter,
} from './importLogsQuickStructuredAdapter';
export { buildNaturalCaptureSchoolRows } from './importLogsSchoolAdapter';

export { normalizeTextForImport } from './importLogsParsingText';
export { normalizeImportCategory, hasStrongSchoolStructureForCategory } from './importLogsParsingClassification';
export { buildSchoolEventChunks } from './importLogsParsingChunks';
export { splitNaturalCaptureLongNoteBlocks as splitLongNoteEmailBlocks } from './naturalCaptureChunkingCore';
export { buildNaturalCaptureCategoryDecision } from './naturalCaptureCategoryCore';

const createNaturalCaptureAdapterInput = ({
  text = '',
  adapterMode = 'auto',
  source = '',
  shapeHint = '',
  childId = null,
  selectedDate = null,
  timezone = '',
  options = {},
} = {}) => ({
  text: String(text || ''),
  adapterMode,
  source,
  shapeHint,
  childId,
  selectedDate,
  timezone,
  options: {
    ...(options || {}),
  },
});

const resolveNaturalCaptureAdapterMode = (adapterInput = {}, {
  parserMode = 'long_note_email',
  sourceMode = 'long_note_email',
  inputMode = 'import',
  rawText = '',
} = {}) => {
  if (
    adapterInput.adapterMode === 'school_pasted_text'
    || parserMode === 'school_text'
    || sourceMode === 'school_text'
    || inputMode === 'school_pasted_text'
  ) {
    return 'school_pasted_text';
  }

  if (
    adapterInput.adapterMode === 'quick_log_structured'
    || parserMode === 'quick_log_structured'
    || sourceMode === 'quick_log_structured'
    || inputMode === 'quick_log_structured'
  ) {
    return 'quick_log_structured';
  }

  if (
    adapterInput.adapterMode === 'imported_file_text'
    || parserMode === 'file_import_text'
    || sourceMode === 'docx_xlsx'
    || inputMode === 'file_import'
  ) {
    return 'imported_file_text';
  }

  if (
    adapterInput.adapterMode === 'long_note_email'
    || parserMode === 'long_note_email'
    || sourceMode === 'long_note_email'
    || inputMode === 'import'
    || isLongNoteEmailText(String(rawText || adapterInput.text || '').trim())
  ) {
    return 'long_note_email';
  }

  if (adapterInput.adapterMode === 'backend_ai_fallback') {
    return 'backend_ai_fallback';
  }

  return null;
};

export const parseNaturalCaptureText = (input = '', {
  defaultDate = new Date(),
  childName = null,
  enableDomainBoundarySplit = false,
  parserMode = 'long_note_email',
  sourceMode = 'long_note_email',
  inputMode = 'import',
  rawText = '',
  adapterMode = 'auto',
} = {}) => {
  const normalizedInput = String(input || '').trim();
  const canonicalRawText = String(rawText || normalizedInput || '').trim();
  const adapterInput = createNaturalCaptureAdapterInput({
    text: normalizedInput,
    adapterMode,
    source: sourceMode,
    shapeHint: detectNaturalCaptureInputShape(normalizedInput, {
      adapterMode,
      parserMode,
      sourceMode,
      inputMode,
    }),
    childId: childName,
    selectedDate: defaultDate,
    timezone: '',
    options: {
      defaultDate,
      childName,
      enableDomainBoundarySplit,
      parserMode,
      sourceMode,
      inputMode,
      rawText: canonicalRawText || normalizedInput,
    },
  });
  const resolvedAdapterMode = resolveNaturalCaptureAdapterMode(adapterInput, {
    parserMode,
    sourceMode,
    inputMode,
    rawText: canonicalRawText || normalizedInput,
  });

  if (!normalizedInput || !resolvedAdapterMode) {
    return {
      rows: [],
      debug: {
        parserUsed: null,
        parserMode,
        sourceMode,
        inputMode,
        adapterMode,
        inputType: 'unsupported',
        detectedShape: adapterInput.shapeHint || 'unknown',
        reason: 'shared_facade_slice_supports_explicit_adapters_only',
        rawTextLength: canonicalRawText.length,
      },
    };
  }

  if (resolvedAdapterMode === 'quick_log_structured') {
    const result = runNaturalCaptureQuickLogAdapter(adapterInput, {
      defaultDate,
      childName,
      parserMode,
      sourceMode,
      inputMode,
    }, {
      extractQuickNoteEntities,
      filterQuickNoteCaptureableEntities,
      normalizeQuickNoteReviewRows,
      groupRowsForReview,
    });
    return {
      ...result,
      debug: {
        ...result.debug,
        detectedShape: adapterInput.shapeHint || 'unknown',
        chunkPlanSource: 'extractQuickNoteEntities',
      },
    };
  }

  if (resolvedAdapterMode === 'school_pasted_text') {
    return runSchoolPastedTextAdapter(adapterInput, {
      defaultDate,
      childName,
      parserMode: 'school_text',
      sourceMode: 'school_text',
      inputMode: inputMode === 'school_pasted_text' ? 'school_pasted_text' : inputMode,
      rawText: canonicalRawText || normalizedInput,
    }, {
      buildNaturalCaptureCategoryDecision,
      applyNaturalCaptureCategoryDecisions,
      emitNaturalCaptureDecisionAudit,
    });
  }

  if (resolvedAdapterMode === 'imported_file_text') {
    return runImportedFileTextAdapter(adapterInput, {
      defaultDate,
      childName,
      parserMode: parserMode === 'file_import_text' ? parserMode : 'file_import_text',
      sourceMode: sourceMode === 'docx_xlsx' ? sourceMode : 'docx_xlsx',
      inputMode: inputMode === 'file_import' ? inputMode : 'file_import',
      rawText: canonicalRawText || normalizedInput,
    }, {
      applyNaturalCaptureCategoryDecisions,
      emitNaturalCaptureDecisionAudit,
    });
  }

  if (resolvedAdapterMode === 'long_note_email') {
    return runLongNoteEmailAdapter(adapterInput, {
      defaultDate,
      childName,
      enableDomainBoundarySplit,
      parserMode,
      sourceMode,
      inputMode,
      rawText: canonicalRawText || normalizedInput,
    });
  }

  if (resolvedAdapterMode === 'backend_ai_fallback') {
    return {
      rows: [],
      debug: {
        parserUsed: null,
        parserMode,
        sourceMode,
        inputMode,
        adapterMode: 'backend_ai_fallback',
        inputType: 'unsupported',
        detectedShape: adapterInput.shapeHint || 'unknown',
        reason: 'backend_ai_fallback_not_implemented_in_local_facade',
        rawTextLength: canonicalRawText.length,
      },
    };
  }

  return {
    rows: [],
    debug: {
      parserUsed: null,
      parserMode,
      sourceMode,
      inputMode,
      adapterMode,
      inputType: 'unsupported',
      detectedShape: adapterInput.shapeHint || 'unknown',
      reason: 'shared_facade_slice_supports_explicit_adapters_only',
      rawTextLength: canonicalRawText.length,
    },
  };
};
